import { useCallback, useEffect, useRef, useState } from 'react';

/** A locally-chosen image file plus an object URL for previewing it. */
export interface JcPendingImage {
    /** Stable key for the staged file (not a storage path). */
    readonly id: string;
    readonly file: File;
    readonly previewURL: string;
}

export interface JcPendingImages {
    readonly images: ReadonlyArray<JcPendingImage>;
    /** Stages files and returns them, e.g. so the caller can reference their ids. */
    readonly add: (files: ReadonlyArray<File>) => JcPendingImage[];
    readonly remove: (id: string) => void;
    readonly clear: () => void;
}

let nextId = 0;

/**
 * Stages image files chosen in the browser for preview, deferring the upload to
 * a later commit point (so cancelling never writes to storage). Each preview
 * object URL is revoked when its image is removed or the component unmounts.
 */
export const useJcPendingImages = (): JcPendingImages => {
    const [images, setImages] = useState<ReadonlyArray<JcPendingImage>>([]);
    const live = useRef(new Map<string, string>());

    useEffect(() => {
        const urls = live.current;
        return () => urls.forEach(url => URL.revokeObjectURL(url));
    }, []);

    const add = useCallback((files: ReadonlyArray<File>): JcPendingImage[] => {
        const staged = files.map(file => ({ id: `pending-${++nextId}`, file, previewURL: URL.createObjectURL(file) }));
        staged.forEach(image => live.current.set(image.id, image.previewURL));
        setImages(previous => [...previous, ...staged]);
        return staged;
    }, []);

    const revoke = (ids: ReadonlyArray<string>) => ids.forEach(id => {
        const url = live.current.get(id);
        if (url !== undefined) {
            URL.revokeObjectURL(url);
            live.current.delete(id);
        }
    });

    const remove = useCallback((id: string) => {
        revoke([id]);
        setImages(previous => previous.filter(image => image.id !== id));
    }, []);

    const clear = useCallback(() => {
        revoke(Array.from(live.current.keys()));
        setImages([]);
    }, []);

    return { images, add, remove, clear };
};

/** A single staged image: choosing another replaces (and revokes) the previous one. */
export const useJcPendingImage = (): readonly [JcPendingImage | null, (file: File) => void, () => void] => {
    const { images, add, clear } = useJcPendingImages();
    const select = useCallback((file: File) => {
        clear();
        add([file]);
    }, [add, clear]);
    return [images[images.length - 1] ?? null, select, clear];
};
